import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import { activeFilterTypeState } from '@/components/filters/filter.state';
import { FilterSearchInput } from '@/components/filters/filter-search-input';
import { useHandleFilters } from '@/components/filters/use-handle-filters';
import { Z_INDEX_FILTERS_DROPDOWN_MENU } from '@/theme/z-index';
import { Button } from '@/ui/button/button';
import { Flexbox, FlexContainer } from '@/ui/flexbox/flexbox';

import type { TeamFilters } from '../team.state';
import {
  appliedTeamsFiltersState,
  selectedTeamsFiltersState,
} from '../team.state';

import { useApplyTeamFiltersFromUrl } from './use-apply-team-filters-from-url';
import { useTeamsFiltersSearchLocationSearch } from './use-teams-filters-location-search';
import { useAtom, useAtomValue } from 'jotai';

export const TeamsFilters = ({
  filtersDisabled,
}: {
  filtersDisabled: boolean;
}) => {
  const { t } = useTranslation('teams');
  const [activeFilterType, setActiveFilterType] = useAtom(
    activeFilterTypeState,
  );

  const appliedFilters = useAtomValue(appliedTeamsFiltersState);

  const activateSearchAfterApplyingFiltersFromUrl = () => {
    if (!appliedFilters.search) return;

    setActiveFilterType('search');
  };

  useApplyTeamFiltersFromUrl({
    isPrefetching: false,
    onFiltersApply: activateSearchAfterApplyingFiltersFromUrl,
  });

  const { updateLocationSearch, resetLocationSearch } =
    useTeamsFiltersSearchLocationSearch();

  const {
    handleSearchInputChange,
    hasAnyActiveFilters,
    clearSearchInput,
    clearSelectedFilters,
    switchToSearchFilter,
  } = useHandleFilters<TeamFilters>({
    appliedFiltersAtom: appliedTeamsFiltersState,
    selectedFiltersAtom: selectedTeamsFiltersState,
    updateLocationSearch,
    resetLocationSearch,
  });

  return (
    <FilterSearchContainer direction="column" gap="15px">
      <Flexbox
        name="filter-buttons-container"
        justify="flex-end"
        gap="10px"
        alignItems="center"
      >
        <FilterSearchInput
          onChange={handleSearchInputChange}
          placeholder={t`searchByName`}
          disabled={filtersDisabled}
          onRemove={clearSearchInput}
          isInputActivated={
            activeFilterType === 'search' || !!appliedFilters.search
          }
          onInputActivate={switchToSearchFilter}
          onInputDeactivate={() => setActiveFilterType(null)}
          defaultValue={appliedFilters.search ?? ''}
        />
      </Flexbox>
      <Flexbox name="filter-buttons" justify="flex-end">
        {hasAnyActiveFilters && (
          <Button variant="underlined" onClick={clearSelectedFilters}>
            {t`clearAll`}
          </Button>
        )}
      </Flexbox>
    </FilterSearchContainer>
  );
};

const FilterSearchContainer = styled(FlexContainer)`
  padding-right: 20px;
  padding-left: 10px;
  margin-top: 24px;
  margin-bottom: 20px;
  position: relative;
  z-index: ${Z_INDEX_FILTERS_DROPDOWN_MENU};
`;
